import { ArrowRight, Info } from "lucide-react";
import useSeo from "@/lib/useSeo";
import { ASSETS, BRAND, STATS } from "@/lib/brand";
import {
  SectionEyebrow,
  SectionHeading,
  SectionWrap,
  FadeUp,
  CtaBand,
} from "@/components/sections/Primitives";

export default function InvestmentPage() {
  useSeo({
    title: "Investment | Pizza Excellente Franchise",
    description:
      "Review Pizza Excellente franchise investment details, including fees, store format, indicative returns, and growth-focused unit economics.",
    path: "/investment",
    ogImage: ASSETS.kitchen_layout,
    ogImageAlt: "Pizza Excellente franchise investment overview",
  });

  return (
    <div data-testid="investment-page">
      {/* HERO */}
      <section className="relative pt-32 md:pt-48 pb-16 md:pb-24 bg-pe-cream overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-14">
          <div className="max-w-4xl">
            <SectionEyebrow>Investment</SectionEyebrow>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-pe-ink mt-6 leading-[1.02]">
              A franchise model designed around <span className="italic text-pe-maroon">practical economics.</span>
            </h1>
            <p className="mt-8 text-pe-ink/70 text-lg leading-relaxed max-w-2xl">
              Pizza Excellente has been structured to balance a premium customer proposition with a compact store format and a disciplined operating model.
            </p>
            <a
              data-testid="investment-hero-cta"
              href={BRAND.mailto}
              className="inline-flex items-center gap-2 mt-10 bg-pe-maroon text-white hover:bg-pe-maroon-deep transition-colors px-8 py-4 text-[12px] tracking-[0.15em] font-medium uppercase"
            >
              Request Investment Details <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </section>

      {/* Section 1 — Headline economics bento */}
      <SectionWrap testId="investment-headline" tone="white">
        <FadeUp className="mb-12 max-w-2xl">
          <SectionEyebrow>Headline economics</SectionEyebrow>
          <SectionHeading className="mt-5">Numbers at a glance.</SectionHeading>
        </FadeUp>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-pe-ink/10">
          {[STATS.fee, STATS.royalty, STATS.term, STATS.investment, STATS.footprint, STATS.mix, STATS.aov, STATS.payback].map((s) => (
            <div key={s.label} className="bg-white p-6 md:p-8">
              <div className="font-serif text-4xl md:text-5xl text-pe-maroon leading-none">{s.value}</div>
              <div className="mt-3 text-[11px] tracking-[0.18em] uppercase text-pe-ink/70">{s.label}</div>
            </div>
          ))}
        </div>
      </SectionWrap>

      {/* Section 2 — Franchise structure */}
      <SectionWrap testId="investment-structure" tone="cream">
        <div className="grid md:grid-cols-12 gap-12 md:gap-20 items-start">
          <FadeUp className="md:col-span-5">
            <SectionEyebrow>Structure</SectionEyebrow>
            <SectionHeading className="mt-5">Franchise structure.</SectionHeading>
          </FadeUp>
          <FadeUp delay={0.1} className="md:col-span-7 space-y-6 text-pe-ink/75 text-lg leading-relaxed">
            <p>
              The franchise fee is <strong className="text-pe-ink">Rs 5 lakhs</strong>, with a <strong className="text-pe-ink">4% royalty</strong>
              {" "}over a <strong className="text-pe-ink">7-year term</strong>.
            </p>
            <p>This structure is intended to support attractive franchise economics while enabling long-term brand development.</p>
          </FadeUp>
        </div>
      </SectionWrap>

      {/* Section 3 — Store Format (Estimated investment content) */}
      <SectionWrap testId="investment-estimated" tone="mauve">
        <div className="grid md:grid-cols-12 gap-12 md:gap-20 items-center">
          <FadeUp className="md:col-span-6">
            <SectionEyebrow>Store Format</SectionEyebrow>
            <SectionHeading className="mt-5">Estimated investment.</SectionHeading>
            <p className="mt-8 text-pe-ink/75 text-lg leading-relaxed">
              The estimated cost to establish a Pizza Excellente unit is approximately <strong className="text-pe-ink">Rs 55 lakhs</strong>,
              including working capital and franchise fees for franchise-operated stores.
            </p>
            <p className="mt-5 text-pe-ink/75 text-lg leading-relaxed">
              A typical store format is around <strong className="text-pe-ink">700 sq ft</strong>, with approximately half allocated to the kitchen
              and half to the front-of-house and dine-in space.
            </p>
          </FadeUp>
          <FadeUp delay={0.1} className="md:col-span-6">
            <div className="bg-white border border-pe-border">
              <img src={ASSETS.kitchen_layout} alt="Pizza Excellente kitchen and front-of-house layout — 700 sq ft footprint" className="w-full" />
              <div className="p-6 border-t border-pe-border">
                <div className="eyebrow text-pe-rose mb-2">Concept layout</div>
                <p className="text-pe-ink/75 leading-relaxed text-sm">
                  Designed by <strong>Nail and Hammer</strong>, a New Delhi-based commercial restaurant designer — engineered for workflow efficiency,
                  space optimisation, safety, and compliance.
                </p>
              </div>
            </div>
          </FadeUp>
        </div>
      </SectionWrap>

      {/* Section 4 — Setup (Store format café content + investment details image) */}
      <SectionWrap testId="investment-format" tone="white">
        <div className="grid md:grid-cols-12 gap-12 md:gap-20 items-start">
          <FadeUp className="md:col-span-5">
            <SectionEyebrow>Setup</SectionEyebrow>
            <SectionHeading className="mt-5">A café for the neighborhood.</SectionHeading>
            <p className="mt-8 text-pe-ink/75 text-lg leading-relaxed">
              The Pizza Excellente model is designed for approximately <strong className="text-pe-ink">70% takeout</strong> and{" "}
              <strong className="text-pe-ink">30% dine-in</strong> — café-style format with quick service, seating for around 20 guests,
              and efficient execution in residential catchments.
            </p>
          </FadeUp>
          <FadeUp delay={0.1} className="md:col-span-7">
            <div className="grid grid-cols-2 gap-px bg-pe-ink/10">
              <div className="bg-white p-7">
                <div className="font-serif text-5xl text-pe-orange leading-none">70%</div>
                <div className="mt-3 text-[11px] tracking-[0.18em] uppercase text-pe-ink/70">Takeout</div>
                <p className="mt-4 text-sm text-pe-ink/65 leading-relaxed">Free delivery within a 1 km radius via in-house staff to contain costs.</p>
              </div>
              <div className="bg-white p-7">
                <div className="font-serif text-5xl text-pe-orange leading-none">30%</div>
                <div className="mt-3 text-[11px] tracking-[0.18em] uppercase text-pe-ink/70">Dine-in</div>
                <p className="mt-4 text-sm text-pe-ink/65 leading-relaxed">Seating for around 20 guests in a warm, café-style room.</p>
              </div>
              <div className="bg-white p-7">
                <div className="font-serif text-5xl text-pe-orange leading-none">~10</div>
                <div className="mt-3 text-[11px] tracking-[0.18em] uppercase text-pe-ink/70">Initial team / store</div>
                <p className="mt-4 text-sm text-pe-ink/65 leading-relaxed">Scaling +1 in Yr 2 and +2 in Yr 3 across BOH/FOH and delivery roles.</p>
              </div>
              <div className="bg-white p-7">
                <div className="font-serif text-5xl text-pe-orange leading-none">Petpooja</div>
                <div className="mt-3 text-[11px] tracking-[0.18em] uppercase text-pe-ink/70">POS platform</div>
                <p className="mt-4 text-sm text-pe-ink/65 leading-relaxed">Ready integrations with leading food-delivery platforms.</p>
              </div>
            </div>
          </FadeUp>
        </div>

        {/* Investment details — title/subtitle + image, appended below the existing content within the Setup section */}
        <FadeUp delay={0.15} className="mt-20 md:mt-24">
          <div className="grid md:grid-cols-12 gap-12 md:gap-20 items-start">
            <div className="md:col-span-5">
              <SectionEyebrow>Investment details</SectionEyebrow>
              <SectionHeading className="mt-5">Where the money goes.</SectionHeading>
            </div>
            <div className="md:col-span-7 text-pe-ink/75 text-lg leading-relaxed">
              <p>
                A category-level breakdown of investment budget — kitchen equipment, front-of-house, fit-out, working capital, and franchise fees.
              </p>
            </div>
          </div>
          <figure className="mt-10 md:mt-14 bg-white border-2 border-pe-aubergine/25 p-4 md:p-6 shadow-sm">
            <img
              src="https://customer-assets.emergentagent.com/job_pizza-community-dev/artifacts/hbvcc2eb_Investment_26.png"
              alt="Pizza Excellente investment details — category budget breakdown"
              className="w-full h-auto"
            />
          </figure>
        </FadeUp>
      </SectionWrap>

      {/* Section 5 — Indicative returns */}
      <SectionWrap testId="investment-returns" tone="ink">
        <div className="grid md:grid-cols-12 gap-12 md:gap-20">
          <FadeUp className="md:col-span-5">
            <SectionEyebrow>
              <span className="text-pe-orange">Returns</span>
            </SectionEyebrow>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-pe-cream mt-5 leading-[1.05]">
              Indicative returns.
            </h2>
            <p className="mt-8 text-pe-cream/75 text-lg leading-relaxed">
              Based on the business-plan assumptions, return on sales approaches <span className="text-pe-orange">20%</span> by year 5
              and return on investment is estimated at around <span className="text-pe-orange">30%</span>.
            </p>
            <p className="mt-5 text-pe-cream/75 text-lg leading-relaxed">
              The projected payback period is approximately <span className="text-pe-orange">3.5 years</span> for both company-operated
              and franchise-operated stores, subject to local market conditions and execution.
            </p>
          </FadeUp>
          <FadeUp delay={0.1} className="md:col-span-7">
            <div className="grid grid-cols-2 gap-px bg-white/10">
              {[
                { v: "~20%", l: "Return on sales (Yr 5)" },
                { v: "~30%", l: "Return on investment" },
                { v: "~3.5 yrs", l: "Projected payback" },
                { v: "15%", l: "Yr-on-yr revenue growth (assumed)" },
                { v: "₹330", l: "Conservative AOV" },
                { v: "26–25%", l: "Labour cost % by Yr 5" },
              ].map((s) => (
                <div key={s.l} className="bg-pe-ink p-7">
                  <div className="font-serif text-4xl md:text-5xl text-pe-orange leading-none">{s.v}</div>
                  <div className="mt-3 text-[11px] tracking-[0.18em] uppercase text-pe-cream/70">{s.l}</div>
                </div>
              ))}
            </div>
          </FadeUp>
        </div>
      </SectionWrap>

      {/* Section 6 — Important note */}
      <SectionWrap testId="investment-note" tone="cream">
        <FadeUp className="max-w-3xl">
          <div className="flex items-start gap-4 bg-white border border-pe-border p-8 md:p-10">
            <Info className="text-pe-maroon shrink-0 mt-1" size={22} strokeWidth={1.5} />
            <div>
              <div className="eyebrow text-pe-maroon">Important note</div>
              <h3 className="font-serif text-2xl md:text-3xl text-pe-ink mt-3 leading-snug">
                All figures are indicative.
              </h3>
              <p className="mt-4 text-pe-ink/75 leading-relaxed">
                Investment and return figures may vary based on city tier, site conditions, lease terms, staffing, and market response.
                Tier 1 cities may run roughly 25–30% higher and Tier 3 cities roughly 10–15% lower than the Tier 2 baseline assumed here.
                Detailed commercial discussions can be shared with qualified prospects during the franchise evaluation process.
              </p>
            </div>
          </div>
        </FadeUp>
      </SectionWrap>

      <CtaBand
        testId="investment-cta-band"
        eyebrow="Discuss the details"
        heading="Speak with our team about the numbers."
        body="Detailed investment disclosures and franchise terms can be shared with qualified prospects during the evaluation process."
        ctaLabel="Contact for Investment Discussion"
        ctaHref={BRAND.mailto}
        secondaryLabel="See Franchise Opportunity"
        secondaryHref="/franchise-opportunity"
      />
    </div>
  );
}
